/**
 * Vault Writer Service - Unified Write Path & Enrichment Pipeline
 * 
 * THIS IS THE SINGLE ENTRY POINT FOR ALL CONTENT WRITES.
 * 
 * Every item saved to Swirl MUST go through this service:
 * - Browser extension drops
 * - Manual uploads
 * - Instagram saves
 * - Notes
 * - Images
 * - Links
 * - Clipper content
 * - API ingestion
 * 
 * This service ensures:
 * 1. Normalized structure (same schema as vaultService)
 * 2. Full enrichment (embeddings, themes, topics)
 * 3. No drift between sources
 * 4. All items are searchable and analyzable
 * 
 * DO NOT bypass this service. Ever.
 */

import { supabase } from '../lib/supabase';
import vaultService from './vaultService';
import enrichmentService from './enrichmentService';
import schemaValidator from './schemaValidator';

/**
 * Apply minimal safe defaults to guarantee schema compliance
 * 
 * This is called when enrichment fails completely.
 * It ensures the item can still be saved without validation errors.
 */
function applyMinimalDefaults(item) {
  const title = item.title || 'Untitled';
  const channel = item.channel || 'Upload';
  
  // GUARANTEE: extractedThemes is non-empty array
  const extractedThemes = Array.isArray(item.extractedThemes) && item.extractedThemes.length > 0
    ? item.extractedThemes
    : ['lifestyle'];
  
  // GUARANTEE: microTopics is non-empty array
  const microTopics = Array.isArray(item.microTopics) && item.microTopics.length > 0
    ? item.microTopics
    : [title || 'saved item'];
  
  // GUARANTEE: entities is an array (can be empty)
  const entities = Array.isArray(item.entities)
    ? item.entities
    : [];
  
  // GUARANTEE: aiAnalysis has required fields
  const aiAnalysis = {
    summary: item.aiAnalysis?.summary && item.aiAnalysis.summary.length >= 20
      ? item.aiAnalysis.summary
      : `${title} is stored in your vault.`,
    whyItMatters: item.aiAnalysis?.whyItMatters ||
      'This was saved for later reference.',
    relatedConcepts: Array.isArray(item.aiAnalysis?.relatedConcepts) && item.aiAnalysis.relatedConcepts.length > 0
      ? item.aiAnalysis.relatedConcepts
      : microTopics.slice(0, 3),
  };
  
  // GUARANTEE: embedding is an array (or null for later generation)
  const embedding = Array.isArray(item.embedding)
    ? item.embedding
    : null;
  
  return {
    ...item,
    extractedThemes,
    microTopics,
    entities,
    aiAnalysis,
    embedding,
    autoTags: {
      topics: microTopics,
      entities,
    },
    meta: {
      ...item.meta,
      minimalDefaultsApplied: true,
      minimalDefaultsTimestamp: new Date().toISOString(),
    },
  };
}

// Removed: Now using enrichmentService for embedding generation

// Removed: Now using enrichmentService for all extraction

/**
 * Normalize raw item to canonical vault schema
 */
function normalizeItem(rawItem, sourceTable) {
  const now = new Date().toISOString();
  
  return {
    // Core identity
    id: rawItem.id || crypto.randomUUID(),
    userId: rawItem.user_id || rawItem.userId,
    
    // Timestamps
    createdAt: rawItem.created_at || rawItem.createdAt || now,
    updatedAt: now,
    
    // Content
    title: rawItem.title || rawItem.filename || 'Untitled',
    description: rawItem.description || rawItem.content?.substring(0, 500) || '',
    textContent: rawItem.raw_text || rawItem.extracted_text || rawItem.textContent || rawItem.content || rawItem.transcription || '',
    
    // Media
    url: rawItem.url || rawItem.file_url || '',
    imageUrl: rawItem.image_url || rawItem.thumbnail_url || rawItem.url || '',
    thumbnailUrl: rawItem.thumbnail_url || rawItem.image_url || rawItem.url || '',
    images: rawItem.images || (rawItem.thumbnail_url ? [rawItem.thumbnail_url] : []),
    
    // Classification
    sourceType: rawItem.source_type || rawItem.content_type || rawItem.file_type || 'unknown',
    channel: rawItem.channel || sourceTable || 'Unknown',
    
    // Tags and metadata
    tags: rawItem.tags || [],
    autoTags: rawItem.auto_tags || { topics: [] },
    meta: rawItem.meta || {},
    
    // Analysis (will be enriched)
    aiAnalysis: rawItem.ai_analysis || rawItem.aiAnalysis || null,
    analysisData: rawItem.analysis_data || rawItem.analysisData || null,
    
    // Enrichment fields (will be populated)
    extractedThemes: rawItem.extractedThemes || [],
    microTopics: rawItem.microTopics || rawItem.auto_tags?.topics || rawItem.tags || [],
    embedding: rawItem.embedding || null,
    
    // Traceability
    originalSourceTable: sourceTable,
    originalSourceId: rawItem.id,
  };
}

// Removed: Now handled by enrichmentService

/**
 * MAIN WRITE FUNCTION
 * 
 * This is the ONLY way to save items to the vault.
 * 
 * @param {Object} rawItem - Raw item from any source
 * @param {Object} options - Write options
 * @param {string} options.sourceTable - Source table name (e.g., 'instagram_content', 'deepsearch.items')
 * @param {boolean} options.skipEnrichment - Skip AI enrichment (for bulk imports)
 * @param {boolean} options.skipEmbedding - Skip embedding generation
 * @returns {Promise<Object>} Saved item with enrichment
 */
export async function saveItem(rawItem, options = {}) {
  const {
    sourceTable = 'deepsearch.items',
    skipEnrichment = false,
    skipEmbedding = false,
  } = options;

  console.log('[VaultWriter] Saving item to:', sourceTable);

  try {
    // Step 1: Normalize to canonical schema
    let item = normalizeItem(rawItem, sourceTable);
    
    // Step 2: FULL ENRICHMENT PIPELINE (with fallback on failure)
    if (!skipEnrichment) {
      console.log('[VaultWriter] Running enrichment pipeline...');
      
      try {
        const enriched = await enrichmentService.enrichItem(item, { 
          skipEmbedding 
        });
        
        // Merge enrichment results into item
        item.extractedThemes = enriched.extractedThemes;
        item.microTopics = enriched.microTopics;
        item.entities = enriched.entities;
        item.aiAnalysis = enriched.aiAnalysis;
        item.embedding = enriched.embedding;
        item.meta = { ...item.meta, ...enriched.meta };
        item.autoTags = { 
          topics: enriched.microTopics,
          entities: enriched.entities,
        };
        
        console.log('[VaultWriter] ✓ Enrichment complete');
        
      } catch (enrichError) {
        console.error('[VaultWriter] ❌ Enrichment failed:', enrichError);
        console.log('[VaultWriter] Applying minimal safe defaults...');
        
        // Apply minimal safe defaults to guarantee schema compliance
        item = applyMinimalDefaults(item);
        
        console.log('[VaultWriter] ✓ Minimal defaults applied');
      }
    } else {
      console.log('[VaultWriter] Skipping enrichment, applying minimal defaults...');
      item = applyMinimalDefaults(item);
    }
    
    // Step 4: SCHEMA VALIDATION (Critical!)
    console.log('[VaultWriter] Validating against canonical schema...');
    
    try {
      schemaValidator.assertValid(item, {
        allowMissingEnrichment: skipEnrichment,
        strict: true,
      });
      console.log('[VaultWriter] ✓ Schema validation passed');
    } catch (validationError) {
      console.error('[VaultWriter] ❌ Schema validation failed:', validationError.validationErrors);
      console.error('[VaultWriter] Item:', item);
      throw new Error(`Schema validation failed: ${validationError.message}`);
    }
    
    // Step 5: Write to PRIMARY TABLE (deepsearch.items)
    // This is the ONLY canonical storage location
    const targetTable = 'items';
    const targetSchema = 'deepsearch';
    const mappedItem = {
      id: item.id,
      user_id: item.userId,
      created_at: item.createdAt,
      updated_at: item.updatedAt,
      title: item.title,
      description: item.description,
      url: item.url,
      image_url: item.imageUrl,
      thumbnail_url: item.thumbnailUrl,
      source_type: item.sourceType,
      channel: item.channel,
      raw_text: item.textContent,
      tags: item.tags,
      auto_tags: item.autoTags,
      meta: {
        ...item.meta,
        ai_analysis: item.aiAnalysis,
        themes: item.extractedThemes,
        micro_topics: item.microTopics,
        entities: item.entities,
      },
      embedding: item.embedding,
      embedding_model: item.embedding ? 'text-embedding-3-large' : null,
    };
    
    // Step 6: Upsert to PRIMARY TABLE ONLY (deepsearch.items)
    console.log('[VaultWriter] Writing to deepsearch.items...');
    
    const { data, error } = await supabase
      .schema(targetSchema)
      .from(targetTable)
      .upsert(mappedItem, { onConflict: 'id' })
      .select()
      .single();
    
    if (error) {
      console.error('[VaultWriter] Database error:', error);
      throw error;
    }
    
    console.log('[VaultWriter] ✅ Saved to deepsearch.items:', item.id);
    
    // Step 7: Invalidate vault cache so reads pick up new item
    vaultService.clearVaultCache(item.userId);
    
    return {
      ...item,
      _saved: true,
      _savedTo: `${targetSchema}.${targetTable}`,
      _validated: true,
    };
    
  } catch (error) {
    console.error('[VaultWriter] Save failed:', error);
    throw error;
  }
}

/**
 * Batch save multiple items
 */
export async function saveItems(rawItems, options = {}) {
  const results = [];
  const errors = [];
  
  for (const rawItem of rawItems) {
    try {
      const saved = await saveItem(rawItem, options);
      results.push(saved);
    } catch (error) {
      errors.push({ item: rawItem, error });
    }
  }
  
  return { results, errors };
}

/**
 * Update existing item with new data
 */
export async function updateItem(itemId, updates, options = {}) {
  const { sourceTable = 'deepsearch.items' } = options;
  
  // Fetch existing item
  let existing;
  if (sourceTable === 'instagram_content') {
    const { data } = await supabase
      .schema('public')
      .from('instagram_content')
      .select('*')
      .eq('id', itemId)
      .single();
    existing = data;
  } else {
    const { data } = await supabase
      .schema('deepsearch')
      .from('items')
      .select('*')
      .eq('id', itemId)
      .single();
    existing = data;
  }
  
  if (!existing) {
    throw new Error(`Item ${itemId} not found in ${sourceTable}`);
  }
  
  // Merge updates and save
  const merged = { ...existing, ...updates };
  return saveItem(merged, { ...options, sourceTable });
}

/**
 * Enrich and upsert a single item
 * 
 * GUARANTEES:
 * - Never throws uncaught errors
 * - Soft-fails on embedding issues
 * - Soft-fails on AI parsing issues
 * - Always returns enrichedItem or null
 */
export async function enrichAndUpsertItem(item) {
  try {
    console.log('[VaultWriter] enrichAndUpsertItem:', item.title?.substring(0, 40) || item.id);
    
    // Validate item has minimum required fields
    if (!item.id) {
      console.warn('[VaultWriter] Item missing ID, skipping');
      return null;
    }
    
    if (!item.userId && !item.user_id) {
      console.warn('[VaultWriter] Item missing userId, skipping');
      return null;
    }
    
    // Normalize userId
    const userId = item.userId || item.user_id;
    
    // Run full enrichment + save
    const saved = await saveItem(item, {
      sourceTable: item.originalSourceTable || 'deepsearch.items',
      skipEnrichment: false,
      skipEmbedding: false,
    });
    
    console.log('[VaultWriter] ✓ Item enriched and saved:', saved.id);
    
    return saved;
    
  } catch (error) {
    // Soft-fail: log error but don't throw
    console.warn('[VaultWriter] ⚠️ enrichAndUpsertItem failed:', error.message);
    console.warn('[VaultWriter] Item ID:', item?.id);
    return null;
  }
}

/**
 * Backfill missing enrichment data
 * 
 * Finds items missing:
 * - embeddings
 * - themes
 * - topics
 * 
 * And enriches them.
 */
export async function backfillMissingFields(userId, options = {}) {
  const { limit = 100, skipEmbedding = false } = options;
  
  console.log('[VaultWriter] Starting backfill for user:', userId?.slice(0, 8));
  
  try {
    // Get all items from vault
    const allItems = await vaultService.getAllItems(userId, { forceRefresh: true });
    
    console.log('[VaultWriter] Found', allItems.length, 'items');
    
    // Find items missing enrichment
    const needsEnrichment = allItems.filter(item => 
      enrichmentService.needsEnrichment(item)
    );
    
    console.log('[VaultWriter] Items needing enrichment:', needsEnrichment.length);
    
    if (needsEnrichment.length === 0) {
      return { enriched: 0, errors: [] };
    }
    
    // Limit batch size
    const batch = needsEnrichment.slice(0, limit);
    
    // Enrich each item
    const results = [];
    const errors = [];
    
    for (const item of batch) {
      try {
        console.log('[VaultWriter] Enriching:', item.title?.substring(0, 50));
        
        // Enrich and save
        const enriched = await saveItem(item, {
          sourceTable: item.originalSourceTable || 'deepsearch.items',
          skipEnrichment: false,
          skipEmbedding,
        });
        
        results.push(enriched);
      } catch (error) {
        console.error('[VaultWriter] Backfill error for item:', item.id, error);
        errors.push({ itemId: item.id, error: error.message });
      }
    }
    
    console.log('[VaultWriter] ✅ Backfill complete:', results.length, 'enriched');
    
    return {
      enriched: results.length,
      errors,
      remaining: needsEnrichment.length - batch.length,
    };
    
  } catch (error) {
    console.error('[VaultWriter] Backfill failed:', error);
    throw error;
  }
}

/**
 * Validate item has all required enrichment
 * Delegates to enrichmentService for quality checks
 */
export function validateEnrichment(item) {
  return enrichmentService.validateEnrichment(item);
}

/**
 * Validate all items in vault have enrichment
 * DEV ONLY - throws error if drift detected
 */
export async function validateVaultIntegrity(userId) {
  if (!import.meta.env?.DEV) {
    console.warn('[VaultWriter] Validation only runs in dev mode');
    return;
  }
  
  const items = await vaultService.getAllItems(userId, { forceRefresh: true });
  const invalid = [];
  
  for (const item of items) {
    const validation = validateEnrichment(item);
    if (!validation.valid) {
      invalid.push({ itemId: item.id, errors: validation.errors });
    }
  }
  
  if (invalid.length > 0) {
    console.error('[VaultWriter] ❌ VAULT INTEGRITY VIOLATION:', invalid);
    console.error('Items missing enrichment:', invalid.length);
    console.error('Run window.backfillVault() to fix');
    
    // In dev, this is a critical error
    if (import.meta.env?.DEV) {
      console.warn('⚠️ Vault has', invalid.length, 'items with missing enrichment');
    }
  } else {
    console.log('[VaultWriter] ✅ Vault integrity validated - all items enriched');
  }
  
  return {
    valid: invalid.length === 0,
    itemsChecked: items.length,
    invalidItems: invalid,
  };
}

/**
 * Expose debug helpers
 */
if (typeof window !== 'undefined') {
  window.vaultWriter = {
    saveItem,
    saveItems,
    updateItem,
    backfillMissingFields,
    validateEnrichment,
    validateVaultIntegrity,
  };
  
  // Main backfill command
  window.backfillVault = async (userId, options = {}) => {
    if (!userId) {
      const { data: { user } } = await supabase.auth.getUser();
      userId = user?.id;
    }
    
    if (!userId) {
      console.error('❌ No user ID provided and not logged in');
      return;
    }
    
    console.log('🔄 Starting vault backfill...');
    const result = await backfillMissingFields(userId, options);
    console.log('✅ Backfill complete:', result);
    return result;
  };
  
  // Alias: window.backfill()
  window.backfill = window.backfillVault;
  
  // Show enriched item
  window.show = async (itemId) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user?.id) {
      console.error('❌ Not logged in');
      return;
    }
    
    const allItems = await vaultService.getAllItems(user.id, { forceRefresh: true });
    const item = allItems.find(i => i.id === itemId);
    
    if (!item) {
      console.error('❌ Item not found:', itemId);
      return;
    }
    
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log('📄 ITEM:', itemId);
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log('');
    console.log('BASIC INFO:');
    console.log('  Title:', item.title);
    console.log('  URL:', item.url);
    console.log('  Source:', item.originalSourceTable);
    console.log('  Created:', item.createdAt);
    console.log('');
    console.log('ENRICHMENT:');
    console.log('  Themes:', item.extractedThemes);
    console.log('  Topics:', item.microTopics);
    console.log('  Entities:', item.entities);
    console.log('  Embedding:', item.embedding ? `✓ (${item.embedding.length} dims)` : '✗ None');
    console.log('');
    if (item.aiAnalysis) {
      console.log('AI ANALYSIS:');
      console.log('  Summary:', item.aiAnalysis.summary);
      console.log('  Why it matters:', item.aiAnalysis.whyItMatters);
      console.log('  Related:', item.aiAnalysis.relatedConcepts);
      console.log('');
    }
    
    const validation = validateEnrichment(item);
    console.log('VALIDATION:', validation.valid ? '✅ PASS' : '❌ FAIL');
    if (!validation.valid) {
      console.log('  Errors:', validation.errors);
    }
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    
    return item;
  };
  
  console.log('[VaultWriter] Debug commands available:');
  console.log('  window.enrich(item) - Test enrichment (from enrichmentService)');
  console.log('  window.backfill() - Backfill missing enrichment');
  console.log('  window.show(itemId) - Show enriched item');
  console.log('  window.vaultWriter - Direct service access');
}

// Default export
export default {
  saveItem,
  saveItems,
  updateItem,
  backfillMissingFields,
  validateEnrichment,
  validateVaultIntegrity,
};

